import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Calendar as DateCalendar } from "@/components/ui/calendar";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import {
  DollarSign, ShoppingBag, CheckCircle, Loader2, Copy,
  Calendar as CalendarIcon, School,
} from "lucide-react";
import { toast } from "sonner";
import { ViewOnlyBanner } from "./ViewOnlyBanner";
import { useState, useEffect } from "react";
import axios from "axios";

export const OrdersTab = ({
  darkMode, orders, orderFilter, setOrderFilter, orderTotals,
  getFilteredOrders, dateRange, setDateRange,
  datePickerOpen, setDatePickerOpen, viewOnly, token
}) => {
  const cardBg = darkMode ? "bg-gray-800/80 border border-gray-700/50" : "";
  const valText = darkMode ? "text-white" : "text-navy-900";
  const lblText = darkMode ? "text-gray-400" : "text-gray-500";

  const handleExportCSV = () => {
    const filtered = getFilteredOrders();
    const csv = "Ref,Customer Name,Email,Phone,Items,Amount (GHS),Status,Date\n" +
      filtered.map(o => `${o.transaction_ref},"${o.customer_name || ""}",${o.customer_email},${o.customer_phone || ""},${(o.items||[]).length},${(o.total_amount||0).toFixed(2)},${o.status},${o.created_at ? new Date(o.created_at).toLocaleDateString("en-GB") : ""}`).join("\n");
    const blob = new Blob([csv], { type: "text/csv" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a"); a.href = url; a.download = `orders_${orderFilter}_${new Date().toISOString().slice(0,10)}.csv`; a.click();
    URL.revokeObjectURL(url);
    toast.success("Orders exported");
  };

  return (
    <>
      {viewOnly && <ViewOnlyBanner darkMode={darkMode} />}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
        {[
          { label: "Total Orders", value: orders.length, icon: ShoppingBag, color: "text-blue-600 bg-blue-50", darkColor: "text-blue-400 bg-blue-900/30", filter: "all" },
          { label: "Completed", value: orderTotals.completed, icon: CheckCircle, color: "text-green-600 bg-green-50", darkColor: "text-green-400 bg-green-900/30", filter: "completed" },
          { label: "Pending", value: orderTotals.pending, icon: Loader2, color: "text-amber-600 bg-amber-50", darkColor: "text-amber-400 bg-amber-900/30", filter: "pending" },
          { label: "Revenue", value: `GHS ${orderTotals.revenue.toFixed(2)}`, icon: DollarSign, color: "text-emerald-600 bg-emerald-50", darkColor: "text-emerald-400 bg-emerald-900/30", filter: null },
        ].map(({ label, value, icon: Icon, color, darkColor, filter }) => {
          const c = darkMode ? darkColor : color;
          return (
            <div
              key={label}
              className={`rounded-2xl p-4 ${c.split(" ")[1]} ${filter !== null ? "cursor-pointer hover:ring-2 hover:ring-offset-1 transition-all" : ""} ${filter !== null && orderFilter === filter ? `ring-2 ring-offset-1 ${darkMode ? "ring-gray-400 ring-offset-gray-900" : "ring-navy-900"}` : ""} ${darkMode ? "ring-offset-gray-900" : ""}`}
              onClick={() => filter !== null && setOrderFilter(filter)}
            >
              <Icon className={`w-5 h-5 ${c.split(" ")[0]} mb-2`} />
              <p className={`text-2xl font-bold ${valText}`}>{value}</p>
              <p className={`text-sm ${lblText}`}>{label}</p>
            </div>
          );
        })}
      </div>
      <Card className={`border-0 shadow-soft rounded-3xl ${cardBg}`}>
        <CardHeader className="flex flex-row items-center justify-between">
          <CardTitle className={`flex items-center gap-2 ${darkMode ? "text-gray-100" : ""}`}><DollarSign className="w-5 h-5" /> {orderFilter === "all" ? "All" : orderFilter.charAt(0).toUpperCase() + orderFilter.slice(1)} Orders</CardTitle>
          <div className="flex items-center gap-2">
            <Popover open={datePickerOpen} onOpenChange={setDatePickerOpen}>
              <PopoverTrigger asChild>
                <Button variant="outline" size="sm" className={`rounded-xl text-xs gap-1.5 ${darkMode ? "bg-gray-700 border-gray-600 text-gray-200 hover:bg-gray-600" : ""}`} data-testid="date-range-picker">
                  <CalendarIcon className="w-3.5 h-3.5" />
                  {dateRange.from ? (
                    dateRange.to
                      ? `${dateRange.from.toLocaleDateString("en-GB", { day: "numeric", month: "short" })} - ${dateRange.to.toLocaleDateString("en-GB", { day: "numeric", month: "short" })}`
                      : dateRange.from.toLocaleDateString("en-GB", { day: "numeric", month: "short", year: "numeric" })
                  ) : "Date Range"}
                </Button>
              </PopoverTrigger>
              <PopoverContent className={`w-auto p-0 rounded-2xl ${darkMode ? "bg-gray-800 border-gray-700" : ""}`} align="end">
                <div className={`p-3 border-b flex items-center justify-between ${darkMode ? "border-gray-700" : ""}`}>
                  <span className={`text-sm font-medium ${darkMode ? "text-gray-200" : ""}`}>Select Range</span>
                  <div className="flex gap-1">
                    {[
                      { label: "7d", days: 7 },
                      { label: "30d", days: 30 },
                      { label: "90d", days: 90 },
                    ].map(({ label, days }) => (
                      <Button key={label} variant="ghost" size="sm" className={`h-7 text-xs rounded-lg px-2 ${darkMode ? "text-gray-300 hover:bg-gray-700" : ""}`}
                        onClick={() => {
                          const to = new Date();
                          const from = new Date(); from.setDate(from.getDate() - days);
                          setDateRange({ from, to }); setDatePickerOpen(false);
                        }}
                      >{label}</Button>
                    ))}
                  </div>
                </div>
                <DateCalendar
                  mode="range"
                  selected={dateRange}
                  onSelect={(range) => { setDateRange(range || { from: null, to: null }); if (range?.to) setDatePickerOpen(false); }}
                  numberOfMonths={2}
                  className="rounded-2xl"
                />
              </PopoverContent>
            </Popover>
            {(orderFilter !== "all" || dateRange.from) && (
              <Button variant="ghost" size="sm" className={`rounded-xl text-xs ${darkMode ? "text-gray-300 hover:bg-gray-700" : ""}`} onClick={() => { setOrderFilter("all"); setDateRange({ from: null, to: null }); }}>
                Clear Filters
              </Button>
            )}
            {!viewOnly && (
            <Button
              variant="outline"
              size="sm"
              className={`rounded-xl ${darkMode ? "bg-gray-700 border-gray-600 text-gray-200 hover:bg-gray-600" : ""}`}
              onClick={handleExportCSV}
              disabled={orders.length === 0}
              data-testid="export-orders"
            >
              <Copy className="w-3.5 h-3.5 mr-1" /> Export CSV
            </Button>
            )}
          </div>
        </CardHeader>
        <CardContent>
          {(() => {
            const filtered = getFilteredOrders();
            return filtered.length > 0 ? (
            <Table>
              <TableHeader>
                <TableRow className={darkMode ? "border-gray-700" : ""}>
                  <TableHead className={darkMode ? "text-gray-400" : ""}>Ref</TableHead>
                  <TableHead className={darkMode ? "text-gray-400" : ""}>Customer</TableHead>
                  <TableHead className={darkMode ? "text-gray-400" : ""}>Items</TableHead>
                  <TableHead className={darkMode ? "text-gray-400" : ""}>Amount</TableHead>
                  <TableHead className={darkMode ? "text-gray-400" : ""}>Status</TableHead>
                  <TableHead className={darkMode ? "text-gray-400" : ""}>Date</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {filtered.map(order => (
                  <TableRow key={order.transaction_ref} className={darkMode ? "border-gray-700/50 hover:bg-gray-700/30" : ""}>
                    <TableCell className={`font-mono text-xs ${darkMode ? "text-gray-300" : ""}`}>{order.transaction_ref}</TableCell>
                    <TableCell>
                      <div>
                        <p className={`font-medium text-sm ${darkMode ? "text-gray-200" : ""}`}>{order.customer_name || "-"}</p>
                        <p className={`text-xs ${darkMode ? "text-gray-500" : "text-gray-500"}`}>{order.customer_email}</p>
                      </div>
                    </TableCell>
                    <TableCell className={`text-sm ${darkMode ? "text-gray-300" : ""}`}>{(order.items || []).length} item(s)</TableCell>
                    <TableCell className={`font-semibold ${darkMode ? "text-white" : ""}`}>GHS {(order.total_amount || 0).toFixed(2)}</TableCell>
                    <TableCell>
                      <Badge variant={order.status === "completed" ? "default" : order.status === "pending" ? "secondary" : "destructive"} className="capitalize rounded-full">
                        {order.status}
                      </Badge>
                    </TableCell>
                    <TableCell className={`text-sm ${darkMode ? "text-gray-400" : "text-gray-500"}`}>
                      {order.created_at ? new Date(order.created_at).toLocaleDateString("en-GB", { day: "numeric", month: "short", year: "numeric" }) : "-"}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          ) : (
            <div className="text-center py-12">
              <DollarSign className={`w-16 h-16 mx-auto mb-4 ${darkMode ? "text-gray-600" : "text-muted-foreground"}`} />
              <p className={darkMode ? "text-gray-400" : "text-muted-foreground"}>No {orderFilter !== "all" ? orderFilter : ""} orders yet</p>
            </div>
          );
          })()}
        </CardContent>
      </Card>

      {/* School Inquiries Section */}
      <SchoolInquiries darkMode={darkMode} token={token} viewOnly={viewOnly} />
    </>
  );
};

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

function SchoolInquiries({ darkMode, token, viewOnly }) {
  const [inquiries, setInquiries] = useState([]);
  const [loading, setLoading] = useState(true);

  const cardBg = darkMode ? "bg-gray-800/80 border border-gray-700/50" : "";
  const valText = darkMode ? "text-white" : "text-navy-900";

  useEffect(() => {
    if (!token) return;
    axios.get(`${API}/admin/inquiries`, { headers: { Authorization: `Bearer ${token}` } })
      .then(res => setInquiries(res.data))
      .catch(() => {})
      .finally(() => setLoading(false));
  }, [token]);

  const updateStatus = async (id, status) => {
    try {
      await axios.put(`${API}/admin/inquiries/${id}/status`, { status }, { headers: { Authorization: `Bearer ${token}` } });
      setInquiries(prev => prev.map(inq => inq.id === id ? { ...inq, status } : inq));
      toast.success("Status updated");
    } catch { toast.error("Failed to update"); }
  };

  const statusColors = {
    pending: "bg-yellow-100 text-yellow-800 dark:bg-yellow-900/30 dark:text-yellow-300",
    contacted: "bg-blue-100 text-blue-800 dark:bg-blue-900/30 dark:text-blue-300",
    quoted: "bg-purple-100 text-purple-800 dark:bg-purple-900/30 dark:text-purple-300",
    completed: "bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-300",
    cancelled: "bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-300",
  };

  return (
    <Card className={`${cardBg} rounded-xl mt-6`} data-testid="school-inquiries-card">
      <CardHeader className="pb-4">
        <div className="flex items-center justify-between">
          <CardTitle className={`flex items-center gap-2 text-base ${valText}`}>
            <School className="w-5 h-5" />
            School Inquiries ({inquiries.length})
          </CardTitle>
        </div>
      </CardHeader>
      <CardContent>
        {loading ? (
          <div className="text-center py-8"><Loader2 className="w-6 h-6 animate-spin mx-auto text-gray-400" /></div>
        ) : inquiries.length === 0 ? (
          <div className="text-center py-8">
            <School className={`w-12 h-12 mx-auto mb-3 ${darkMode ? "text-gray-600" : "text-gray-300"}`} />
            <p className={`text-sm ${darkMode ? "text-gray-400" : "text-muted-foreground"}`}>No school inquiries yet</p>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>School</TableHead>
                  <TableHead>Contact</TableHead>
                  <TableHead>Subjects</TableHead>
                  <TableHead>Qty</TableHead>
                  <TableHead>Format</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead>Date</TableHead>
                  {!viewOnly && <TableHead>Action</TableHead>}
                </TableRow>
              </TableHeader>
              <TableBody>
                {inquiries.map(inq => (
                  <TableRow key={inq.id} data-testid={`inquiry-row-${inq.id}`}>
                    <TableCell>
                      <div className={`font-medium text-sm ${valText}`}>{inq.school_name}</div>
                      <div className={`text-xs ${darkMode ? "text-gray-400" : "text-muted-foreground"}`}>{inq.region}</div>
                    </TableCell>
                    <TableCell>
                      <div className={`text-sm ${valText}`}>{inq.contact_person}</div>
                      <div className={`text-xs ${darkMode ? "text-gray-400" : "text-muted-foreground"}`}>{inq.email}</div>
                      {inq.phone && <div className={`text-xs ${darkMode ? "text-gray-400" : "text-muted-foreground"}`}>{inq.phone}</div>}
                    </TableCell>
                    <TableCell>
                      <div className="flex flex-wrap gap-1">
                        {(inq.subjects || []).slice(0, 2).map(s => (
                          <Badge key={s} variant="secondary" className="text-[10px] px-1.5">{s}</Badge>
                        ))}
                        {(inq.subjects || []).length > 2 && <Badge variant="secondary" className="text-[10px] px-1.5">+{inq.subjects.length - 2}</Badge>}
                      </div>
                    </TableCell>
                    <TableCell className={`text-sm ${valText}`}>{inq.quantity || "—"}</TableCell>
                    <TableCell className={`text-sm ${valText}`}>{inq.format_preference || "—"}</TableCell>
                    <TableCell>
                      <Badge className={`${statusColors[inq.status] || statusColors.pending} border-0 text-xs`}>
                        {inq.status}
                      </Badge>
                    </TableCell>
                    <TableCell className={`text-xs ${darkMode ? "text-gray-400" : "text-muted-foreground"}`}>
                      {inq.created_at ? new Date(inq.created_at).toLocaleDateString("en-GB", { day: "numeric", month: "short" }) : "—"}
                    </TableCell>
                    {!viewOnly && (
                      <TableCell>
                        <Select value={inq.status} onValueChange={(v) => updateStatus(inq.id, v)}>
                          <SelectTrigger className={`h-7 text-xs w-28 ${darkMode ? "bg-gray-700 border-gray-600 text-white" : ""}`}><SelectValue /></SelectTrigger>
                          <SelectContent>
                            <SelectItem value="pending">Pending</SelectItem>
                            <SelectItem value="contacted">Contacted</SelectItem>
                            <SelectItem value="quoted">Quoted</SelectItem>
                            <SelectItem value="completed">Completed</SelectItem>
                            <SelectItem value="cancelled">Cancelled</SelectItem>
                          </SelectContent>
                        </Select>
                      </TableCell>
                    )}
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        )}
      </CardContent>
    </Card>
  );
}
